import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { showToast } from '@components/toaster';
import { showDialog } from '@components/dialog';
import { BtnSecondary } from '@components/buttons';
import { IcoLock } from '@components/icons';
import { useCurrentUser } from '@components/router/session-context';
import { hasLevel } from 'shared/auth';
import { SetPasswordEmailKind } from 'shared/set-password-email';
import { useState } from 'preact/hooks';

type Student = { id: string; name: string; email: string };

/**
 * Staff tools for a student who has not set up their account, reachable from
 * the course roster where staff actually look (Lizzie, 2026-10-21). The
 * endpoints are admin-only: a set-password link opens the account.
 */
export function useCanManageSetPassword() {
  return hasLevel(useCurrentUser(), 'admin');
}

export async function copySetPasswordLink(student: Student, courseId: string) {
  try {
    const result = await rpx.admin.getSetPasswordLink({ userId: student.id, courseId });
    if (!result.url) {
      showToast({
        title: 'No link for this account',
        message: `Sign-in links are not issued for superadmin accounts.`,
        type: 'warn',
      });
      return;
    }
    await navigator.clipboard.writeText(result.url);
    showToast({
      title: 'Link copied',
      message: `Anyone with this link can sign in as ${student.name} once, so share it only with them.`,
      type: 'ok',
    });
  } catch (err) {
    showError(err);
  }
}

const KIND_LABEL: Record<SetPasswordEmailKind, string> = {
  initial: 'welcome email',
  reminder: 'reminder email',
};

export async function resendSetPasswordEmail(
  student: Student,
  courseId: string,
  kind: SetPasswordEmailKind = 'initial',
) {
  const label = KIND_LABEL[kind];
  const confirmed = await showDialog({
    mode: 'info',
    title: `Send the ${label}?`,
    children: `${student.name} (${student.email}) will get the course's ${label}.`,
    confirmButtonText: 'Send email',
  });
  if (!confirmed) {
    return;
  }
  try {
    const result = await rpx.admin.resendSetPasswordEmail({ userId: student.id, courseId, kind });
    if (result.sent) {
      showToast({
        title: 'Email sent',
        message: `The ${label} was sent to ${student.email}.`,
        type: 'ok',
      });
    } else {
      showToast({
        title: 'Not sent',
        message:
          result.reason === 'has_password'
            ? `${student.name} already has a password; use Send reminder, which signs them in.`
            : 'The email could not be sent. Check the server logs.',
        type: 'warn',
      });
    }
  } catch (err) {
    showError(err);
  }
}

export function SetPasswordActions({ student, courseId }: { student: Student; courseId: string }) {
  const canManage = useCanManageSetPassword();
  if (!canManage) {
    return null;
  }
  return (
    <div class="flex flex-wrap gap-2 mt-3">
      <BtnSecondary onClick={() => copySetPasswordLink(student, courseId)}>
        <IcoLock class="w-4 h-4 mr-1 opacity-50" />
        Copy sign-in link
      </BtnSecondary>
      <BtnSecondary onClick={() => resendSetPasswordEmail(student, courseId, 'initial')}>
        Re-send welcome email
      </BtnSecondary>
      <BtnSecondary onClick={() => resendSetPasswordEmail(student, courseId, 'reminder')}>
        Send reminder
      </BtnSecondary>
    </div>
  );
}

/**
 * A small "Copy link" next to "Never" on the Progress tab. The row itself is a
 * link to the student panel, so the click must not bubble.
 */
export function CopySetPasswordLinkInline({
  student,
  courseId,
}: {
  student: Student;
  courseId: string;
}) {
  const canManage = useCanManageSetPassword();
  if (!canManage) {
    return null;
  }
  return (
    <button
      type="button"
      class="text-indigo-600 text-xs underline hover:text-indigo-800"
      title="Copy this student's set-password link"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        copySetPasswordLink(student, courseId);
      }}
    >
      Copy link
    </button>
  );
}

/**
 * "Send reminder to everyone not set up" on the Progress tab. Confirms with
 * the count; the server sends in the background and refuses a second run
 * while one is in progress.
 */
export function SendRemindersButton({ courseId, count }: { courseId: string; count: number }) {
  const canManage = useCanManageSetPassword();
  const [isSending, setIsSending] = useState(false);
  if (!canManage || count === 0) {
    return null;
  }
  const send = async () => {
    const confirmed = await showDialog({
      mode: 'warn',
      title: `Send the reminder to ${count} ${count === 1 ? 'person who has' : 'people who have'} never opened the course?`,
      children:
        'Everyone in this course who has never opened it will get the reminder email. People without a password get their set-password link; people who already have one get a Sign in button. Edit the wording first under Settings → Notifications if you have not.',
      confirmButtonText: 'Send reminders',
    });
    if (!confirmed) {
      return;
    }
    setIsSending(true);
    try {
      const result = await rpx.admin.sendSetPasswordReminders({ courseId });
      showToast({
        title: 'Reminders on their way',
        message: `${result.queued} ${result.queued === 1 ? 'email is' : 'emails are'} being sent now.`,
        type: 'ok',
      });
    } catch (err) {
      showError(err);
    } finally {
      setIsSending(false);
    }
  };
  return (
    <BtnSecondary isLoading={isSending} onClick={send}>
      Send reminder to {count} who never opened
    </BtnSecondary>
  );
}
